import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiArrowLeft, FiChevronRight, FiHome, FiFileText} = FiIcons;

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb Navigation */}
      <div className="bg-slate-50 border-b border-slate-200 pt-24 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-2 text-sm">
            <Link 
              to="/" 
              className="flex items-center space-x-1 text-slate-500 hover:text-orange-600 transition-colors duration-300"
            >
              <SafeIcon icon={FiHome} className="text-xs" />
              <span>Home</span>
            </Link>
            <SafeIcon icon={FiChevronRight} className="text-slate-400 text-xs" />
            <span className="text-slate-900 font-medium">Terms of Service</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-orange-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-white"
          >
            <SafeIcon icon={FiFileText} className="text-4xl text-orange-400 mb-6 mx-auto" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-slate-300">
              Last updated: January 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            className="prose prose-lg max-w-none"
          >
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg mb-8">
              <p className="text-slate-700 mb-0">
                Please read these Terms of Service carefully before using our website and services. 
                By accessing or using WeDesignedIt's services, you agree to be bound by these terms.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement. 
                  If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Services Description</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  WeDesignedIt provides web design, development, digital marketing, and related services to businesses. 
                  Our services include but are not limited to:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Website design and development</li>
                  <li>Mobile application development</li>
                  <li>Digital marketing services</li>
                  <li>Brand design and identity</li>
                  <li>E-commerce solutions</li>
                  <li>Consulting services</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">3. User Responsibilities</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  You are responsible for:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Providing accurate and complete information</li>
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>Ensuring all content you provide is legal and appropriate</li>
                  <li>Complying with all applicable laws and regulations</li>
                  <li>Respecting intellectual property rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Payment Terms</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Payment terms are specified in individual project agreements. Generally:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Deposits may be required before work commences</li>
                  <li>Payment schedules are agreed upon in project contracts</li>
                  <li>Late payments may incur additional charges</li>
                  <li>Refunds are subject to our refund policy</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Intellectual Property</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Upon full payment, clients receive ownership of custom-developed work products. However:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>WeDesignedIt retains rights to general methodologies and know-how</li>
                  <li>Third-party components remain owned by their respective owners</li>
                  <li>WeDesignedIt may showcase completed work in portfolios</li>
                  <li>Clients must respect third-party intellectual property rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Limitation of Liability</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  WeDesignedIt's liability is limited to the amount paid for services. We are not liable for:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Indirect, incidental, or consequential damages</li>
                  <li>Loss of profits or business opportunities</li>
                  <li>Third-party actions or content</li>
                  <li>Force majeure events beyond our control</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Privacy and Data Protection</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We are committed to protecting your privacy and complying with GDPR and UK data protection laws. 
                  Please see our <Link to="/privacy-policy" className="text-orange-600 hover:text-orange-700">Privacy Policy</Link> for detailed information.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Termination</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  Either party may terminate services with appropriate notice as specified in project agreements. 
                  Upon termination:
                </p>
                <ul className="list-disc pl-6 text-slate-700 space-y-2">
                  <li>Outstanding payments become immediately due</li>
                  <li>Work in progress may be delivered in current state</li>
                  <li>Confidentiality obligations continue</li>
                  <li>Certain provisions of these terms survive termination</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Governing Law</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  These terms are governed by the laws of England and Wales. Any disputes will be subject to 
                  the exclusive jurisdiction of the English courts.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Changes to Terms</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  We reserve the right to modify these terms at any time. Changes will be posted on this page 
                  with an updated revision date. Continued use of our services constitutes acceptance of modified terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Contact Information</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  If you have questions about these Terms of Service, please contact us:
                </p>
                <div className="bg-slate-50 p-6 rounded-lg">
                  {/* <p className="text-slate-700 mb-2">
                    <strong>WeDesignedIt</strong><br />
                    59 Walton St, Aylesbury HP21 7QG<br />
                    United Kingdom
                  </p> */}
                  <p className="text-slate-700">
                    Email: <a href="mailto:hello@wedesignedit.co.uk" className="text-orange-600 hover:text-orange-700">hello@wedesignedit.co.uk</a><br />
                    {/* Phone: <a href="tel:+447769061291" className="text-orange-600 hover:text-orange-700">07769061291</a> */}
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Related Legal Documents</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link 
              to="/privacy-policy" 
              className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Privacy Policy</h4>
              <p className="text-slate-600">Learn how we collect, use, and protect your personal information.</p>
            </Link>
            <Link 
              to="/cookie-policy" 
              className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <h4 className="text-lg font-semibold text-slate-900 mb-2">Cookie Policy</h4>
              <p className="text-slate-600">Understand how we use cookies and similar technologies.</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;